import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import Tooltip from "@mui/material/Tooltip";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HPButton from "../common/Button";
import { SortingOption } from "@store/slices/crosstab-config-slice";

interface RowColumnItem {
  id: string;
  label: string;
  type: "variable" | "value";
}

interface CrosstabCustomizationPanelProps {
  rows: RowColumnItem[];
  columns: RowColumnItem[];
  hiddenRows: string[];
  hiddenColumns: string[];
  // Ids (same convention as row.id/col.id above) whose dataset source is now
  // hidden — distinct from the editor-local hiddenRows/hiddenColumns toggle.
  hiddenInDatasetRowIds?: Set<string>;
  hiddenInDatasetColumnIds?: Set<string>;
  customRowLabels: Record<string, string>;
  customColumnLabels: Record<string, string>;
  rowOrder: string[];
  columnOrder: string[];
  onToggleRowVisibility: (id: string) => void;
  onToggleColumnVisibility: (id: string) => void;
  onUpdateRowLabel: (id: string, label: string) => void;
  onUpdateColumnLabel: (id: string, label: string) => void;
  onResetRowLabel: (id: string) => void;
  onResetColumnLabel: (id: string) => void;
  onShowAllRows: () => void;
  onHideAllRows: () => void;
  onShowAllColumns: () => void;
  onHideAllColumns: () => void;
  onReorderRows: (fromIndex: number, toIndex: number) => void;
  onReorderColumns: (fromIndex: number, toIndex: number) => void;
  sortingOption: SortingOption;
  onUpdateSortingOption: (option: SortingOption) => void;
}

// Sortable Row Item Component
interface SortableRowItemProps {
  item: RowColumnItem;
  isHidden: boolean;
  isHiddenInDataset?: boolean;
  hasCustomLabel: boolean;
  displayLabel: string;
  isEditing: boolean;
  editValue: string;
  isDragEnabled: boolean;
  onToggleVisibility: (id: string) => void;
  onStartEdit: (item: RowColumnItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onResetLabel: (id: string) => void;
  onEditValueChange: (value: string) => void;
}

const SortableRowItem: React.FC<SortableRowItemProps> = ({
  item,
  isHidden,
  isHiddenInDataset = false,
  hasCustomLabel,
  displayLabel,
  isEditing,
  editValue,
  isDragEnabled,
  onToggleVisibility,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onResetLabel,
  onEditValueChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !isDragEnabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        backgroundColor: isHidden ? "#f5f5f5" : "white",
        opacity: isHidden ? 0.6 : 1,
        cursor: isDragging ? "grabbing" : "default",
      }}
    >
      {/* Drag handle */}
      {isDragEnabled && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            color: "#666",
            "&:active": {
              cursor: "grabbing",
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      )}

      {/* Visibility toggle */}
      <IconButton
        size="small"
        onClick={() => onToggleVisibility(item.id)}
        sx={{ flexShrink: 0 }}
      >
        {isHidden ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>

      {/* Label or edit field */}
      {isEditing ? (
        <Box sx={{ flex: 1 }}>
          <input
            type="text"
            value={editValue}
            onChange={(e) => onEditValueChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onSaveEdit();
              if (e.key === "Escape") onCancelEdit();
            }}
            autoFocus
            className="w-full px-3 py-2 border border-[#D9D9D9] rounded-lg text-sm outline-none focus:border-[#1976d2] transition-colors"
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: hasCustomLabel ? 600 : 400,
            }}
          >
            {displayLabel}
          </Typography>
          {hasCustomLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              Original: {item.label}
            </Typography>
          )}
        </Box>
      )}

      {/* Dataset-hidden marker */}
      {isHiddenInDataset && (
        <Tooltip title="Hidden in dataset">
          <ReportProblemOutlinedIcon
            fontSize="small"
            sx={{ color: "warning.main", flexShrink: 0 }}
          />
        </Tooltip>
      )}

      {/* Action buttons */}
      {isEditing ? (
        <>
          <IconButton size="small" onClick={onSaveEdit} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onCancelEdit}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <>
          <IconButton
            size="small"
            onClick={() => onStartEdit(item)}
            disabled={isHidden}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {hasCustomLabel && (
            <HPButton
              size="small"
              variant="text"
              onClick={() => onResetLabel(item.id)}
            >
              Reset
            </HPButton>
          )}
        </>
      )}
    </Box>
  );
};

const CrosstabCustomizationPanel: React.FC<CrosstabCustomizationPanelProps> = ({
  rows,
  columns,
  hiddenRows,
  hiddenColumns,
  hiddenInDatasetRowIds,
  hiddenInDatasetColumnIds,
  customRowLabels,
  customColumnLabels,
  rowOrder,
  columnOrder,
  onToggleRowVisibility,
  onToggleColumnVisibility,
  onUpdateRowLabel,
  onUpdateColumnLabel,
  onResetRowLabel,
  onResetColumnLabel,
  onShowAllRows,
  onHideAllRows,
  onShowAllColumns,
  onHideAllColumns,
  onReorderRows,
  onReorderColumns,
  sortingOption,
  onUpdateSortingOption,
}) => {
  const [rowSearchTerm, setRowSearchTerm] = useState("");
  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editRowValue, setEditRowValue] = useState("");
  const [editColumnValue, setEditColumnValue] = useState("");

  // Setup drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle row drag end
  const handleRowDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rowOrder.indexOf(active.id as string);
      const newIndex = rowOrder.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderRows(oldIndex, newIndex);
      }
    }
  };

  // Handle column drag end
  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        onReorderColumns(oldIndex, newIndex);
      }
    }
  };

  // Order rows based on rowOrder, then filter by search
  const orderedAndFilteredRows = useMemo(() => {
    // Create a map for quick lookup
    const rowsMap = new Map(rows.map((row) => [row.id, row]));

    // Order rows according to rowOrder
    const orderedRows = rowOrder
      .map((id) => rowsMap.get(id))
      .filter((row): row is RowColumnItem => row !== undefined);

    // Filter by search term
    if (!rowSearchTerm) return orderedRows;
    const searchLower = rowSearchTerm.toLowerCase();
    return orderedRows.filter((row) => {
      const displayLabel = customRowLabels[row.id] || row.label;
      return displayLabel.toLowerCase().includes(searchLower);
    });
  }, [rows, rowOrder, rowSearchTerm, customRowLabels]);

  // Order columns based on columnOrder, then filter by search
  const orderedAndFilteredColumns = useMemo(() => {
    // Create a map for quick lookup
    const columnsMap = new Map(columns.map((col) => [col.id, col]));

    // Order columns according to columnOrder
    const orderedColumns = columnOrder
      .map((id) => columnsMap.get(id))
      .filter((col): col is RowColumnItem => col !== undefined);

    // Filter by search term
    if (!columnSearchTerm) return orderedColumns;
    const searchLower = columnSearchTerm.toLowerCase();
    return orderedColumns.filter((col) => {
      const displayLabel = customColumnLabels[col.id] || col.label;
      return displayLabel.toLowerCase().includes(searchLower);
    });
  }, [columns, columnOrder, columnSearchTerm, customColumnLabels]);

  // Count visible items
  const visibleRowsCount = rows.filter(
    (r) => !hiddenRows.includes(r.id),
  ).length;
  const visibleColumnsCount = columns.filter(
    (c) => !hiddenColumns.includes(c.id),
  ).length;

  // Show All only targets eligible (not dataset-hidden) members — once every
  // eligible row/column is visible, the button has nothing left to do.
  const eligibleRowsCount = rows.filter(
    (r) => !hiddenInDatasetRowIds?.has(r.id),
  ).length;
  const eligibleColumnsCount = columns.filter(
    (c) => !hiddenInDatasetColumnIds?.has(c.id),
  ).length;

  // Handle row edit
  const handleStartEditRow = (row: RowColumnItem) => {
    setEditingRowId(row.id);
    setEditRowValue(customRowLabels[row.id] || row.label);
  };

  const handleSaveRowEdit = () => {
    if (editingRowId && editRowValue.trim()) {
      onUpdateRowLabel(editingRowId, editRowValue.trim());
    }
    setEditingRowId(null);
    setEditRowValue("");
  };

  const handleCancelRowEdit = () => {
    setEditingRowId(null);
    setEditRowValue("");
  };

  const handleResetRowLabel = (id: string) => {
    onResetRowLabel(id);
    if (editingRowId === id) {
      setEditingRowId(null);
      setEditRowValue("");
    }
  };

  // Handle column edit
  const handleStartEditColumn = (col: RowColumnItem) => {
    setEditingColumnId(col.id);
    setEditColumnValue(customColumnLabels[col.id] || col.label);
  };

  const handleSaveColumnEdit = () => {
    if (editingColumnId && editColumnValue.trim()) {
      onUpdateColumnLabel(editingColumnId, editColumnValue.trim());
    }
    setEditingColumnId(null);
    setEditColumnValue("");
  };

  const handleCancelColumnEdit = () => {
    setEditingColumnId(null);
    setEditColumnValue("");
  };

  const handleResetColumnLabel = (id: string) => {
    onResetColumnLabel(id);
    if (editingColumnId === id) {
      setEditingColumnId(null);
      setEditColumnValue("");
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <FormLabel className="text-[12px] text-[#262626]">Sorting</FormLabel>
        <RadioGroup
          row
          value={sortingOption}
          onChange={(e) =>
            onUpdateSortingOption(e.target.value as SortingOption)
          }
        >
          <FormControlLabel
            value="original"
            control={<Radio size="small" />}
            label="Keep original order"
          />
          <FormControlLabel
            value="manual"
            control={<Radio size="small" />}
            label="Manual Order"
          />
        </RadioGroup>
      </Box>

      {/* Row Customization */}
      <Accordion
        defaultExpanded
        sx={{ boxShadow: "none", border: "1px solid #eee" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Row Customization
            </Typography>
            <Chip
              label={`${visibleRowsCount}/${rows.length} visible`}
              size="small"
              color={visibleRowsCount === rows.length ? "default" : "primary"}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onShowAllRows}
                disabled={visibleRowsCount === eligibleRowsCount}
              >
                Show All
              </HPButton>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onHideAllRows}
                disabled={visibleRowsCount === 0}
              >
                Hide All
              </HPButton>
            </Box>

            <Divider />

            {/* Row list with DND */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleRowDragEnd}
            >
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {orderedAndFilteredRows.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No rows found
                  </Typography>
                ) : (
                  <SortableContext
                    items={rowOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedAndFilteredRows.map((row) => {
                      const isHidden = hiddenRows.includes(row.id);
                      const hasCustomLabel = !!customRowLabels[row.id];
                      const displayLabel = customRowLabels[row.id] || row.label;
                      const isEditing = editingRowId === row.id;

                      return (
                        <SortableRowItem
                          key={row.id}
                          item={row}
                          isHidden={isHidden}
                          isHiddenInDataset={hiddenInDatasetRowIds?.has(
                            row.id,
                          )}
                          hasCustomLabel={hasCustomLabel}
                          displayLabel={displayLabel}
                          isEditing={isEditing}
                          editValue={editRowValue}
                          isDragEnabled={sortingOption === "manual"}
                          onToggleVisibility={onToggleRowVisibility}
                          onStartEdit={handleStartEditRow}
                          onSaveEdit={handleSaveRowEdit}
                          onCancelEdit={handleCancelRowEdit}
                          onResetLabel={handleResetRowLabel}
                          onEditValueChange={setEditRowValue}
                        />
                      );
                    })}
                  </SortableContext>
                )}
              </Box>
            </DndContext>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 2 }} />

      {/* Column Customization */}
      <Accordion
        defaultExpanded
        sx={{ boxShadow: "none", border: "1px solid #eee" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Column Customization
            </Typography>
            <Chip
              label={`${visibleColumnsCount}/${columns.length} visible`}
              size="small"
              color={
                visibleColumnsCount === columns.length ? "default" : "primary"
              }
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onShowAllColumns}
                disabled={visibleColumnsCount === eligibleColumnsCount}
              >
                Show All
              </HPButton>
              <HPButton
                size="small"
                variant="outlined"
                onClick={onHideAllColumns}
                disabled={visibleColumnsCount === 0}
              >
                Hide All
              </HPButton>
            </Box>

            <Divider />

            {/* Column list with DND */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleColumnDragEnd}
            >
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {orderedAndFilteredColumns.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 2 }}
                  >
                    No columns found
                  </Typography>
                ) : (
                  <SortableContext
                    items={columnOrder}
                    strategy={verticalListSortingStrategy}
                  >
                    {orderedAndFilteredColumns.map((col) => {
                      const isHidden = hiddenColumns.includes(col.id);
                      const hasCustomLabel = !!customColumnLabels[col.id];
                      const displayLabel =
                        customColumnLabels[col.id] || col.label;
                      const isEditing = editingColumnId === col.id;

                      return (
                        <SortableRowItem
                          key={col.id}
                          item={col}
                          isHidden={isHidden}
                          isHiddenInDataset={hiddenInDatasetColumnIds?.has(
                            col.id,
                          )}
                          hasCustomLabel={hasCustomLabel}
                          displayLabel={displayLabel}
                          isEditing={isEditing}
                          editValue={editColumnValue}
                          isDragEnabled={sortingOption === "manual"}
                          onToggleVisibility={onToggleColumnVisibility}
                          onStartEdit={handleStartEditColumn}
                          onSaveEdit={handleSaveColumnEdit}
                          onCancelEdit={handleCancelColumnEdit}
                          onResetLabel={handleResetColumnLabel}
                          onEditValueChange={setEditColumnValue}
                        />
                      );
                    })}
                  </SortableContext>
                )}
              </Box>
            </DndContext>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default CrosstabCustomizationPanel;
